import moment from 'moment'
import { isValidLanguage } from '../i18n-route'

// Re-exported so existing call sites keep working while the whitelist lives
// in exactly one place.
export { isValidLanguage }

export const getStoredLanguage = (): string | null => {
  return localStorage.getItem('currentLanguage')
}

export const setStoredLanguage = (language: string): void => {
  localStorage.setItem('currentLanguage', language)
}

// The router registers a reader here so getLanguage() can prefer the URL
// without utils importing the router (which would be circular:
// router -> store -> api -> utils).
let routeLangGetter: () => string | null = () => null

export const registerRouteLangGetter = (fn: () => string | null): void => {
  routeLangGetter = fn
}

export const getBrowserLanguage = (): string => {
  const nav = navigator as Navigator & { userLanguage?: string }
  const browserLang = navigator.language || nav.userLanguage || ''
  const browserLangCode = browserLang.split('-')[0].toLowerCase()

  // Derived from the single whitelist (isValidLanguage) rather than a second
  // copy of the locale list, so the two cannot drift apart.
  return isValidLanguage(browserLangCode) ? browserLangCode : 'en'
}

// Resolution order: URL param -> localStorage -> browser -> 'en'
export const getLanguage = (): string => {
  const fromUrl = routeLangGetter()
  if (isValidLanguage(fromUrl) && fromUrl) return fromUrl

  const storedLanguage = getStoredLanguage()
  if (isValidLanguage(storedLanguage) && storedLanguage) {
    return storedLanguage
  }

  const browserLanguage = getBrowserLanguage()
  setStoredLanguage(browserLanguage)
  return browserLanguage
}

/**
 * Lấy format hiển thị date theo locale
 * @param locale - Locale code (mặc định: lấy từ localStorage)
 * @returns Format string: 'YYYY-MM-DD' cho ja, 'MM/DD/YYYY' cho các ngôn ngữ khác
 */
export const getDateDisplayFormat = (locale?: string): string => {
  const currentLocale = locale || getLanguage()
  return currentLocale === 'ja' ? 'YYYY-MM-DD' : 'MM/DD/YYYY'
}

/**
 * Lấy format hiển thị month theo locale
 * @param locale - Locale code (mặc định: lấy từ localStorage)
 * @returns Format string: 'YYYY-MM' cho ja, 'MM/YYYY' cho các ngôn ngữ khác
 */
export const getMonthDisplayFormat = (locale?: string): string => {
  const currentLocale = locale || getLanguage()
  return currentLocale === 'ja' ? 'YYYY-MM' : 'MM/YYYY'
}

/**
 * Format month từ YYYY-MM sang format hiển thị theo locale
 * @param month - Month string dạng YYYY-MM
 * @param locale - Locale code (mặc định: lấy từ localStorage)
 * @returns Month string đã format hoặc null nếu month không hợp lệ
 */
export const formatMonthForDisplay = (
  month: string | null,
  locale?: string
): string | null => {
  if (!month) return month

  const format = getMonthDisplayFormat(locale)
  // Thêm ngày 01 để parse như date
  const date = `${month}-01`
  const parsed = moment(date, 'YYYY-MM-DD', true)
  if (!parsed.isValid()) return month

  return parsed.format(format)
}

/**
 * Lấy mask pattern cho date input theo locale
 * @param locale - Locale code (mặc định: lấy từ localStorage)
 * @returns Mask string: '####-##-##' cho ja, '##/##/####' cho các ngôn ngữ khác
 */
export const getDateMask = (locale?: string): string => {
  const currentLocale = locale || getLanguage()
  return currentLocale === 'ja' ? '####-##-##' : '##/##/####'
}

/**
 * Format date từ YYYY-MM-DD sang format hiển thị theo locale
 * @param date - Date string dạng YYYY-MM-DD
 * @param locale - Locale code (mặc định: lấy từ localStorage)
 * @returns Date string đã format hoặc null nếu date không hợp lệ
 */
export const formatDateForDisplay = (
  date: string | null,
  locale?: string
): string | null => {
  if (!date) return date

  const format = getDateDisplayFormat(locale)

  return moment(date).format(format)
}

/**
 * Parse date từ format hiển thị về YYYY-MM-DD
 * @param displayDate - Date string theo format hiển thị
 * @param locale - Locale code (mặc định: lấy từ localStorage)
 * @returns Date string dạng YYYY-MM-DD hoặc null nếu không parse được
 */
export const parseDateFromDisplay = (
  displayDate: string | null,
  locale?: string
): string | null => {
  if (!displayDate) return null

  const format = getDateDisplayFormat(locale)
  const parsed = moment(displayDate, format, true)
  if (!parsed.isValid()) return null

  return parsed.format('YYYY-MM-DD')
}

export const getDateTimeDisplayFormat = (locale?: string): string => {
  const currentLocale = locale || getLanguage()
  return currentLocale === 'ja' ? 'YYYY-MM-DD HH:mm' : 'MM/DD/YYYY HH:mm'
}

export const formatDateTimeForDisplay = (
  dateTime: string | null,
  locale?: string
): string | null => {
  if (!dateTime) return dateTime

  const format = getDateTimeDisplayFormat(locale)

  return moment(dateTime).format(format)
}

export const getDayOfWeekDisplayFormat = (locale?: string): string => {
  const currentLocale = locale || getLanguage()
  return currentLocale === 'ja' ? 'DD日（dd）' : 'DD (ddd)'
}

export const formatDayOfWeekForDisplay = (
  date: string | null,
  locale?: string
): string => {
  if (!date) return ''

  const format = getDayOfWeekDisplayFormat(locale)

  return moment(date).format(format)
}
